import { Notify } from 'quasar'

/**
 * Map backend bucket names to user-friendly feature names
 */
const QUOTA_FEATURE_NAMES = {
  characterDescription: 'Character Description',
  campaignDescription: 'Campaign Description',
  characterImport: 'Character Sheet Import',
  characterAvatar: 'Character Avatar',
  characterGallery: 'Character Gallery Image',
  campaignImage: 'Campaign Image',
  audioTranscription: 'Audio Transcription',
  audioGeneration: 'Audio Generation',
  videoGeneration: 'Video Generation'
}

/**
 * Extract bucket name from error message
 * Backend sends errors like: "Monthly characterAvatar limit reached"
 */
function extractBucketFromError(errorMessage) {
  if (!errorMessage) return null

  const msg = String(errorMessage).toLowerCase()

  // Try to extract bucket name from common error formats
  for (const [bucket] of Object.entries(QUOTA_FEATURE_NAMES)) {
    if (msg.includes(bucket.toLowerCase())) {
      return bucket
    }
  }

  // Check for generic patterns
  if (msg.includes('character') && msg.includes('avatar')) return 'characterAvatar'
  if (msg.includes('character') && msg.includes('description')) return 'characterDescription'
  if (msg.includes('campaign') && msg.includes('description')) return 'campaignDescription'
  if (msg.includes('character') && msg.includes('import')) return 'characterImport'

  return null
}

/**
 * Check if error is a storage quota exceeded error
 */
export function isStorageQuotaError(error) {
  if (!error) return false

  // Check for flag we set in aiService.js
  if (error?.storageQuotaExceeded === true) return true

  const errorMessage =
    typeof error === 'string' ? error : error?.message || error?.reason || String(error)
  const errorMessageLower = errorMessage.toLowerCase()

  // Check for various storage quota error patterns
  return (
    errorMessageLower.includes('not enough storage') ||
    (errorMessageLower.includes('storage') && errorMessageLower.includes('available')) ||
    (errorMessageLower.includes('storage') &&
      (errorMessageLower.includes('quota') ||
        errorMessageLower.includes('limit') ||
        errorMessageLower.includes('exceeded')))
  )
}

/**
 * Check if error is a quota exceeded error (AI usage quota)
export function isQuotaError(error) {
  if (!error) return false

  // Extract message from various error formats (FirebaseError, plain Error, string, object)
  let errorMessage = ''
  if (typeof error === 'string') {
    errorMessage = error
  } else if (error?.message) {
    errorMessage = error.message
  } else if (error?.error?.message) {
    errorMessage = error.error.message
  } else {
    errorMessage = String(error)
  }

  const errorMessageLower = errorMessage.toLowerCase()

  // Check various status/code locations for Firebase callable errors
  const code = error?.code || error?.error?.code || ''
  const status = error?.status || error?.error?.status || error?.details?.status || ''

  console.log('[isQuotaError] Checking error:', {
    errorMessage,
    errorMessageLower,
    code,
    status,
    errorType: error?.constructor?.name,
    rawError: error
  })

  // Check if it's a quota error by status code, error code, or message content
  const isQuota =
    status === 'RESOURCE_EXHAUSTED' ||
    status === 'resource-exhausted' ||
    code === 'functions/resource-exhausted' ||
    code === 'resource-exhausted' ||
    errorMessageLower.includes('limit reached') ||
    errorMessageLower.includes('quota exceeded') ||
    errorMessageLower.includes('usage limit') ||
    (errorMessageLower.includes('monthly') && errorMessageLower.includes('limit'))

  console.log('[isQuotaError] Result:', isQuota)
  return isQuota
}

/**
 * Show user-friendly storage quota exceeded notification with upgrade CTA
 * Returns true if handled as storage quota error, false otherwise
 */
export function handleStorageQuotaError(error) {
  if (!isStorageQuotaError(error)) {
    return false
  }

  const errorMessage =
    typeof error === 'string' ? error : error?.message || error?.reason || String(error)

  Notify.create({
    type: 'warning',
    message: 'Storage quota exceeded',
    caption: errorMessage || 'Upgrade your plan for more storage space.',
    position: 'top',
    timeout: 0, // Persistent until dismissed
    icon: 'warning',
    color: 'warning',
    actions: [
      {
        label: 'View Plans',
        color: 'white',
        handler: () => {
          window.location.href = '/grants'
        }
      },
      {
        label: 'Dismiss',
        color: 'white',
        flat: true
      }
    ]
  })

  return true
}

/**
 * Show user-friendly quota exceeded notification with upgrade CTA
 * Returns true if handled as quota error, false otherwise
 */
export function handleQuotaError(error) {
  // Try storage quota first
  if (handleStorageQuotaError(error)) {
    return true
  }

  // Then check AI usage quota
  if (!isQuotaError(error)) {
    return false
  }

  const errorMessage = error?.message || error?.error?.message || String(error)
  const bucket = extractBucketFromError(errorMessage)
  const featureName = QUOTA_FEATURE_NAMES[bucket] || 'AI feature'

  const message = `Monthly ${featureName} limit reached`
  const caption = 'Upgrade your plan or purchase a grant to continue using this feature.'

  Notify.create({
    type: 'warning',
    message,
    caption,
    position: 'top',
    timeout: 0, // Persistent until dismissed
    icon: 'warning',
    color: 'warning',
    actions: [
      {
        label: 'View Plans',
        color: 'white',
        handler: () => {
          // Use window.location for navigation since we can't inject router here
          window.location.href = '/grants'
        }
      },
      {
        label: 'Dismiss',
        color: 'white',
        flat: true
      }
    ]
  })

  return true
}

/**
 * Wrapper for AI operations that handles quota errors automatically
 * Use this in composables or components for automatic quota error handling
 */
export async function withQuotaErrorHandling(operation) {
  try {
    return await operation()
  } catch (error) {
    // If it's a quota error, show the notification and re-throw
    if (handleQuotaError(error)) {
      throw error
    }
    // Not a quota error, let normal error handling take over
    throw error
  }
}
